/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MaterialPicker, HuePicker } from "react-color";
import { Link, Prompt, useParams } from "react-router-dom";
import landingEmptyLogo from "images/landing-page/landing-empty-logo.png";
import landingPreviewBody from "images/landing-page/landing-preview-body.png";
import landingPreviewHeaderBG from "images/landing-page/landing-preview-header-bg.png";
import landingPreviewHeader from "images/landing-page/landing-preview-header.png";

import { makeGet } from "@culturehq/client";
import { Button, Form, Icon, ImageField, Loader, StringField, SubmitButton, Tooltip, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, useFieldsLabels, useLandingPage } from "app/hooks";
import { getOrganizationSubdomain, getImage, getTags, t, Trans } from "app/utils";

import Accordian from "components/common/Accordian";
import imagesSizes from "constants/imagesSizes";
import ErrorMessage from "components/common/ErrorMessage";
import LazyEditor from "components/editor/LazyEditor";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import ChqModalConfirm from "components/common/ChqModalConfirm";
import ApproveProfileModal from "./ApproveProfileModal";
import ApproveStoryModal from "./ApproveStoryModal";

const ApproveSection = ({
  landingPage, onSelectedStories, onSelectedUsers, setApprovingContent, onLandingPageUpdated
}) => (
  <div className="row Approve">
    <div className="col-xs-12">
      <h4 className="Approve-Header"><Trans>Select Profile Spotlights</Trans></h4>
      <ApproveProfileModal
        landingPage={landingPage}
        onSelectedUsers={onSelectedUsers}
        onLandingPageUpdated={onLandingPageUpdated}
      />
      <h4 className="Approve-Header"><Trans>Select Story Spotlights</Trans></h4>
      <ApproveStoryModal
        landingPage={landingPage}
        onSelectedStories={onSelectedStories}
        setApprovingContent={setApprovingContent}
      />
    </div>
  </div>
);

const DesingSection = ({ editorRef, error, onChange, landingPage, organization, subdomain }) => {
  const [selectedLogo, setSelectedLogo] = useState(landingPage?.logoUrl);
  const [selectedHeaderImage, setSelectedHeaderImage] = useState(landingPage?.headerImageUrl);
  const [headerText, setHeaderText] = useState(landingPage?.headerText);
  const [route, setRoute] = useState(landingPage?.route);
  const [welcomeNote, setWelcomeNote] = useState(landingPage?.welcomeNote);
  const [color, setColor] = useState(landingPage?.primaryColor || "#79b17d");
  const { landingPageRoutes } = organization;

  useEffect(() => {
    if (!landingPage) return;

    setSelectedLogo(landingPage?.logoUrl);
    setSelectedHeaderImage(landingPage?.headerImageUrl);
    setHeaderText(landingPage?.headerText);
    setRoute(landingPage?.route);
    setWelcomeNote(landingPage?.welcomeNote);
    setColor(landingPage?.primaryColor);
  }, [landingPage]);

  const handleOnLogoChange = image => {
    onChange("logoUrl", image);
    setSelectedLogo(image);
  };

  const handleOnHeaderImageChange = image => {
    onChange("headerImageUrl", image);
    setSelectedHeaderImage(image);
  };

  const getAltImageText = () => (selectedLogo?.alt_description || "Preview");

  const handleChange = (color, _) => {
    onChange("primaryColor", color.hex);
    setColor(color.hex);
  };

  const onHeaderTextChange = value => {
    onChange("headerText", value);
    setHeaderText(value);
  };

  const sanitizeRoute = value => (
    value
      .toLowerCase()
      // Replace spaces and underscores with hyphens
      .replace(/[\s_]+/g, "-")
      // Remove characters that shouldn't be in URL routes
      .replace(/[^a-z0-9-]/g, "")
      // Remove consecutive hyphens
      .replace(/-+/g, "-")
      // Only remove leading hyphens, keep trailing ones for now (user might still be typing)
      .replace(/^-+/g, "")
  );

  const onRouteChange = value => {
    // Remove the leading slash if present for sanitization
    const cleanValue = value.startsWith("/") ? value.slice(1) : value;
    const sanitizedValue = sanitizeRoute(cleanValue);

    // Don't allow empty routes (or routes that are just hyphens)
    if (sanitizedValue === "-") return;

    // Create the route with leading slash
    const route = `/${sanitizedValue}`;
    setRoute(route);
    onChange("route", route);
  };

  const onWelcomeNoteChange = value => {
    onChange("welcomeNote", value);
    setWelcomeNote(value);
  };

  const getSelectedHeaderImage = () => {
    if (typeof selectedHeaderImage === "string") {
      return selectedHeaderImage;
    }

    return URL.createObjectURL(selectedHeaderImage);
  };

  const getSelectedLogo = () => {
    if (typeof selectedLogo === "string") {
      return selectedLogo;
    }

    return URL.createObjectURL(selectedLogo);
  };

  const validateRoute = value => {
    if (landingPageRoutes.includes(value) && landingPage?.route !== value) return "This route is already in use by another Culture Page. Please choose a different one.";
    if (value !== "/users" && value !== "/trend" && value !== "/story") return null;
    return `The route could not be called "${value}"`;
  };

  const root = useMemo(() => {
    const getDomain = () => {
      if (process.env.NODE_ENV === "production") return "culturehq.com";
      return "localhost:4000";
    };

    const domain = getDomain();
    return `${subdomain}.${domain}`;
  }, [subdomain]);

  return (
    <div className="row Design">
      <div className="col-md-6">
        {error && <ErrorMessage error={error} />}
        <div className="logo-field">
          <h4><Trans>Logo*</Trans></h4>
          <ImageField
            aspectRatio={1}
            buttonLabel="Upload logo"
            className={selectedLogo && "selected-image"}
            imageAsBackground
            name="logo"
            onChange={handleOnLogoChange}
            required
            value={getImage(imagesSizes.NORMAL, selectedLogo)}
            alt={getAltImageText()}
          />
        </div>
        <div className="header-field">
          <h4><Trans>Header Image*</Trans></h4>
          <ImageField
            aspectRatio={5}
            buttonLabel="Upload an image"
            className={selectedHeaderImage && "selected-image"}
            imageAsBackground
            name="headerImage"
            onChange={handleOnHeaderImageChange}
            required
            value={getImage(imagesSizes.NORMAL, selectedHeaderImage)}
            alt={getAltImageText()}
          />
        </div>
        <h4 className="header-text-field"><Trans>Header Text*</Trans></h4>
        <StringField max="45" name="headerText" value={headerText} required onChange={onHeaderTextChange} />
        <h4><Trans>Welcome Note*</Trans></h4>
        <LazyEditor
          sections={["inlineStyleAndLink"]}
          initialValue={welcomeNote}
          onChange={onWelcomeNoteChange}
          name="welcomeNote"
          ref={editorRef}
        />
        <div className="color-field">
          <h4><Trans>Primary Color*</Trans></h4>
          <MaterialPicker name="color" onChange={handleChange} color={color} />
          <HuePicker onChange={handleChange} color={color} />
        </div>
        <h4 className="header-text-field"><Trans>Custom Page URL*</Trans></h4>
        <div className="string-field-container">
          <span>{root}</span>
          <StringField
            max="20"
            name="route"
            required
            value={route}
            onChange={onRouteChange}
            validator={validateRoute}
          />
        </div>
      </div>
      <div className="col-md-6 Design-Preview">
        <a className="Design-Preview__Link" href="https://demo.culturehq.com" target="_blank" rel="noopener noreferrer">
          <Icon icon="page" />
          <Trans>View an example culture page here</Trans>
        </a>
        <div className="Preview">
          <img className="Preview-Browser" src={landingPreviewHeader} alt={t("Browser header")} />
          <div className="Preview-Header__Image">
            <img className="Preview-Header__Image--Header" src={selectedHeaderImage ? getSelectedHeaderImage() : landingPreviewHeaderBG} alt={t("Browser header")} />
            <div className="Preview-Header__Overlay">
              <h1 className="Preview-Header__Text" style={{ backgroundImage: `linear-gradient(120deg, ${color} 0%, ${color}38 100%)` }}>
                {headerText || t("Enter your header text here...")}
              </h1>
            </div>
          </div>
          <div className="Preview-Body">
            <div className="Preview-Browser__Welcome">
              <div className="Preview-Welcome" style={{ borderColor: `${color}` }}>
                <Icon className="Preview-Welcome__Icon" color={color} icon="quote" />
                <LazyEditorOutput className="Preview-Welcome__Note" output={welcomeNote || " "} showTags={false} />
              </div>
            </div>
            <img className="Preview-Browser__Body" src={landingPreviewBody} alt={t("Browser body")} />
            <img className="Preview-Logo" src={selectedLogo ? getSelectedLogo() : landingEmptyLogo} alt={t("Logo image")} />
          </div>
        </div>
      </div>
    </div>
  );
};

const EditLandingPage = ({ landingPage, onSubmitted, botToken }) => {
  const { profile, fetchedProfile } = useProfile();
  const {
    editorRef,
    loading,
    isLive,
    isReady,
    changed,
    error,
    valuesLocal,
    setIsLive,
    setIsReady,
    setChanged,
    setValuesLocal,
    setApprovingContent,
    setSelectedUsers,
    setSelectedStories,
    handleSubmit,
    handleIsLive
  } = useLandingPage({
    landingPage,
    onSubmitted
  });

  const ref = useRef(null);
  const labels = useFieldsLabels();
  const [landingPageName, setLandingPaneName] = useState(landingPage?.name || "Culture Page Name");
  const [subdomain, setSubdomain] = useState();

  const link = useMemo(() => {
    const getDomain = () => {
      if (process.env.NODE_ENV === "production") return "culturehq.com";
      return "localhost:4000";
    };

    const domain = getDomain();
    const link = `${subdomain}.${domain}${landingPage?.route || "/"}`;

    if (process.env.NODE_ENV === "production") return `https://${link}`;
    return `http://${link}`;
  }, [landingPage, subdomain]);

  useEffect(
    () => {
      localStorage.setItem("departmentsLabel", JSON.stringify(labels.departments.plural.toLowerCase()));
      localStorage.setItem("companiesLabel", JSON.stringify(labels.companies.plural.toLowerCase()));
    }, [labels]
  );

  useEffect(
    () => {
      const orgName = getOrganizationSubdomain(profile.organization);
      const cleanStagingWord = orgName.replace("-staging", "");
      setSubdomain(cleanStagingWord);
      localStorage.setItem("orgName", JSON.stringify(cleanStagingWord));
      localStorage.setItem("isEducational", JSON.stringify(profile.organization.isEducational));
    }, [profile.organization]
  );

  useEffect(
    () => {
      const setLocalStorage = values => {
        Object.keys(values).forEach(key => {
          localStorage.setItem(key, JSON.stringify(values[key]));
        });
        localStorage.setItem("isLogoBlob", JSON.parse(false));
        localStorage.setItem("isHeaderImageBlob", JSON.parse(false));
      };
      if (landingPage) {
        const data = valuesLocal;

        let ready = true;
        Object.keys(data).forEach(key => {
          if (!landingPage[key] && landingPage[key] !== false) {
            ready = false;
          }
        });
        setIsReady(ready);
        setValuesLocal({
          logoUrl: landingPage.logoUrl,
          headerImageUrl: landingPage.headerImageUrl,
          headerText: landingPage.headerText || "",
          route: landingPage.route || "",
          welcomeNote: landingPage.welcomeNote || "",
          primaryColor: landingPage.primaryColor || "#79b17d",
          name: landingPage.name || ""
        });
        setIsLive(landingPage.live);
        setLocalStorage({
          ...landingPage,
          stories: landingPage.stories?.slice(0, 24) || [],
          users: landingPage.users?.slice(0, 24) || []
        });
      } else {
        setLocalStorage(valuesLocal);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [landingPage]
  );

  // Focus on the title name h1 element when component first loads and put cursor at the end
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();

      // Move cursor to the end of the text
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(ref.current);
      range.collapse(false); // false means collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  const onChange = useCallback((key, value) => {
    setChanged(true);
    const data = valuesLocal;
    data[key] = value;

    let ready = true;
    Object.keys(data).forEach(key => {
      if (!data[key]) {
        ready = false;
      } else if (key === "route") {
        ready = data[key] !== "/users" && data[key] !== "/trend" && data[key] !== "/story";
      }
    });
    setIsReady(ready);

    if (key === "logoUrl") {
      const reader = new FileReader();

      reader.onload = event => {
        localStorage.setItem("logoUrl", event.target.result);
      };

      reader.readAsDataURL(value);
      localStorage.setItem("isLogoBlob", JSON.parse(true));
    } else if (key === "headerImageUrl") {
      const reader = new FileReader();

      reader.onload = event => {
        localStorage.setItem("headerImageUrl", event.target.result);
      };

      reader.readAsDataURL(value);
      localStorage.setItem("isHeaderImageBlob", JSON.parse(true));
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    setValuesLocal(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChanged, valuesLocal, setValuesLocal]);

  const onSelectedUsers = selectedUsers => {
    localStorage.setItem("users", JSON.stringify(selectedUsers));
    setSelectedUsers(selectedUsers);
  };

  const onSelectedStories = selectedStories => {
    localStorage.setItem("stories", JSON.stringify(selectedStories));
    setSelectedStories(selectedStories);
  };

  const getDecodedText = () => {
    if (!ref.current) return "";

    const html = ref.current.innerHTML;

    // Remove any remaining HTML tags
    const tempElement = document.createElement("div");
    tempElement.innerHTML = html;
    const decodedText = tempElement.innerText;
    return decodedText;
  };

  const handleUpdateName = async () => {
    if (ref.current) {
      const decodedText = getDecodedText();
      setChanged(true);
      setLandingPaneName(decodedText);
      onChange("name", decodedText);
    }
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (ref.current) ref.current.blur();
    }
  };

  const tags = getTags(
    landingPage?.values,
    landingPage?.interests,
    landingPage?.skills,
    landingPage?.classYears,
    landingPage?.clubs
  );

  if (loading) return <Loader loading />;

  const handleFormSubmit = values => {
    const sanitizedValues = {
      ...values,
      name: landingPageName,
      route: values.route.replace(/-+$/g, "")
    };

    let newRoutes = [...profile.organization.landingPageRoutes];
    if (landingPage) {
      newRoutes = newRoutes.map(route => (
        route === landingPage.route ? sanitizedValues.route : route
      ));
    } else {
      newRoutes.push(sanitizedValues.route);
    }
    const newProfile = {
      ...profile,
      organization: {
        ...profile.organization,
        landingPageRoutes: newRoutes
      }
    };

    fetchedProfile(newProfile);

    handleSubmit(sanitizedValues);
  };

  return (
    <>
      <Prompt
        when={changed}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <div>
        <Form onSubmit={handleFormSubmit} initialValues={landingPage}>
          <div className="LandingPage--Edit">
            <div>
              <Link className="back-arrow" to="/admin/landing-pages">
                <Icon icon="arrow-left-c" />
                <Trans>Go Back</Trans>
              </Link>
              <div className="LandingPage--Title__Container">
                <h1
                  className="LandingPage--Title"
                  contentEditable
                  name="name"
                  onBlur={handleUpdateName}
                  onKeyDown={handleKeyDown}
                  ref={ref}
                  dangerouslySetInnerHTML={{ __html: landingPageName }}
                />
                {isLive
                  ? <span className="LandingPage--Tag Live"><Trans>ACTIVE</Trans></span>
                  : <span className="LandingPage--Tag"><Trans>DRAFT</Trans></span>}
              </div>
              {isLive
                ? <a className="LandingPage--Link__Filled" href={link} target="_blank" rel="noopener noreferrer">{link} <Icon icon="android-open" /></a>
                : <a className="LandingPage--Link__Empty" style={{ pointerEvents: "none" }} href={link} target="_blank" rel="noopener noreferrer">{link}</a>}
            </div>
            <div className="LandingPage--Edit_Buttons">
              <Link
                className="chq-btn Gray-Button"
                to="/admin/landing-page/preview"
                target="_blank"
              >
                <Icon icon="preview-filled" />
                <Trans>Preview Page</Trans>
              </Link>
              {isLive && (
                <Button
                  className="Gray-Button"
                  icon="pause"
                  onClick={() => handleIsLive(false)}
                >
                  <Trans>Unpublish</Trans>
                </Button>
              )}
              <SubmitButton className={isLive ? "" : "Gray-Button"} icon="checkmark">
                {submitting => (submitting ? t("Saving...") : t("Save and Close"))}
              </SubmitButton>
              {(!landingPage || !isLive) && (
                <>
                  {isReady
                    ? (
                      <ChqModalConfirm
                        onAccept={() => handleIsLive(true)}
                        trigger={onTrigger => (
                          <Button icon="play-filled" fillParent onClick={onTrigger}>
                            <Trans>Publish</Trans>
                          </Button>
                        )}
                      >
                        <Trans>Are you ready to set your page live with these changes?</Trans>
                      </ChqModalConfirm>
                    ) : (
                      <Tooltip bottom tip={t("Finish creating your page before setting it live")}>
                        <Button icon="play-filled" fillParent disabled>
                          <Trans>Publish</Trans>
                        </Button>
                      </Tooltip>
                    )}
                </>
              )}
            </div>
          </div>
          <Accordian
            icon="art-palette"
            title={t("Design Page")}
            subtitle={t("Add your logo, header image and text, and primary color.")}
            className="base-menu"
          >
            <DesingSection
              editorRef={editorRef}
              error={error}
              onChange={onChange}
              landingPage={landingPage}
              subdomain={subdomain}
              organization={profile.organization}
            />
          </Accordian>
          <Accordian
            icon="file-filled"
            title={t("Select Content")}
            subtitle={t("Choose specific profiles and stories to showcase on your external page.")}
            className="base-menu"
          >
            <ApproveSection
              landingPage={landingPage}
              onSelectedStories={onSelectedStories}
              onSelectedUsers={onSelectedUsers}
              setApprovingContent={setApprovingContent}
              onLandingPageUpdated={onSubmitted}
            />
          </Accordian>
        </Form>
      </div>
    </>
  );
};

const EmployerBranding = () => {
  const { id: landingPageId = "new" } = useParams();
  const [landingPage, setLandingPage] = useState(undefined);

  const { fetching, json: { landingPage: landingPageData, botToken } } = useFetch(
    () => makeGet(`/landing_pages/${landingPageId}`),
    [landingPageId]
  );

  const handleUpdatedLandingPage = updated => {
    setLandingPage(updated);
  };

  if (fetching) {
    return <Loader loading />;
  }

  return (
    <div className="LandingPage">
      <EditLandingPage
        landingPage={landingPage || landingPageData}
        botToken={botToken}
        onSubmitted={handleUpdatedLandingPage}
      />
    </div>
  );
};

export default EmployerBranding;
